"use client";

import React, { useState, useRef, useEffect } from "react";
import { Slider } from "src/components/ui/slider";
import { Button } from "src/components/ui/button";
import { Switch } from "src/components/ui/switch";
import { Tooltip } from "src/components/ui/tooltip";
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Settings,
  Subtitles,
  PictureInPicture,
  Bookmark,
  ChevronRight,
  SkipForward,
  SkipBack,
  ExternalLink,
  Check,
} from "lucide-react";
import { ConfigProvider, Drawer, Tabs, Popover, Badge } from "antd";
import { cn } from "src/lib/utils";

const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }

  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const CustomPlayer = ({
  sources,
  poster,
  title = "Video",
  subtitles = [],
  chapters = [],
  autoPlay = false,
  loop = false,
  muted = false,
  theme = "dark",
  onTimeUpdate,
  onEnded,
  onError,
}) => {
  // Refs
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const progressBarRef = useRef(null);
  const thumbnailRef = useRef(null);

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(muted ? 0 : 1);
  const [isMuted, setIsMuted] = useState(muted);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [selectedQuality, setSelectedQuality] = useState(
    sources[0]?.quality || "720p"
  );
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [showSubtitles, setShowSubtitles] = useState(false);
  const [selectedSubtitle, setSelectedSubtitle] = useState(null);
  const [isPipActive, setIsPipActive] = useState(false);
  const [isMiniplayer, setIsMiniplayer] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(theme === "dark");
  const [showThumbnail, setShowThumbnail] = useState(false);
  const [thumbnailPosition, setThumbnailPosition] = useState(0);
  const [hideControlsTimeout, setHideControlsTimeout] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(null);

  // Effects
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      onTimeUpdate?.(video.currentTime);

      // Update current chapter
      const newChapter =
        chapters.find((chapter, index) => {
          const nextChapterStartTime =
            chapters[index + 1]?.startTime || Infinity;
          return (
            video.currentTime >= chapter.startTime &&
            video.currentTime < nextChapterStartTime
          );
        }) || null;

      if (newChapter?.id !== currentChapter?.id) {
        setCurrentChapter(newChapter);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onEnded?.();
    };

    const handleError = (e) => {
      console.error("Video error:", e);
      onError?.(e);
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("error", handleError);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handleError);
    };
  }, [chapters, currentChapter, onEnded, onError, onTimeUpdate]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!videoContainerRef.current) return;

      // Only handle shortcuts if the video container is focused or active
      if (
        !videoContainerRef.current.contains(document.activeElement) &&
        document.activeElement !== document.body
      )
        return;

      switch (e.key.toLowerCase()) {
        case " ":
          e.preventDefault();
          togglePlay();
          break;
        case "k":
          togglePlay();
          break;
        case "f":
          toggleFullscreen();
          break;
        case "m":
          toggleMute();
          break;
        case "arrowright":
          skip(10);
          break;
        case "arrowleft":
          skip(-10);
          break;
        case "arrowup":
          changeVolume(Math.min(1, volume + 0.1));
          break;
        case "arrowdown":
          changeVolume(Math.max(0, volume - 0.1));
          break;
        case "i":
          togglePictureInPicture();
          break;
        case "b":
          addBookmark();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [volume, isPlaying]);

  // Handle auto-hide controls
  useEffect(() => {
    const container = videoContainerRef.current;
    if (!container) return;

    const handleMouseMove = () => {
      setShowControls(true);

      if (hideControlsTimeout) {
        clearTimeout(hideControlsTimeout);
      }

      const timeout = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);

      setHideControlsTimeout(timeout);
    };

    const handleMouseLeave = () => {
      if (isPlaying) {
        setShowControls(false);
      }
    };

    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseleave", handleMouseLeave);
      if (hideControlsTimeout) {
        clearTimeout(hideControlsTimeout);
      }
    };
  }, [hideControlsTimeout, isPlaying]);

  // Video control functions
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.play();
      setIsPlaying(true);
    }
  };

  const changeVolume = (newVolume) => {
    const video = videoRef.current;
    if (!video) return;

    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    video.volume = clampedVolume;
    setVolume(clampedVolume);

    if (clampedVolume === 0) {
      setIsMuted(true);
      video.muted = true;
    } else {
      setIsMuted(false);
      video.muted = false;
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      video.muted = false;
      setIsMuted(false);
      if (volume === 0) {
        changeVolume(0.5);
      }
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  };

  const seek = (time) => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = Math.max(0, Math.min(duration, time));
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skip = (seconds) => {
    const video = videoRef.current;
    if (!video) return;

    seek(video.currentTime + seconds);
  };

  const toggleFullscreen = () => {
    const container = videoContainerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      container
        .requestFullscreen()
        .then(() => {
          setIsFullscreen(true);
        })
        .catch((err) => {
          console.error(
            `Error attempting to enable fullscreen: ${err.message}`
          );
        });
    } else {
      document
        .exitFullscreen()
        .then(() => {
          setIsFullscreen(false);
        })
        .catch((err) => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
    }
  };

  const changeQuality = (quality) => {
    const video = videoRef.current;
    if (!video) return;

    const currentTime = video.currentTime;
    const wasPlaying = !video.paused;

    setSelectedQuality(quality);

    // Find the source with the selected quality
    const source = sources.find((s) => s.quality === quality);
    if (source) {
      video.src = source.src;
      video.currentTime = currentTime;

      if (wasPlaying) {
        video
          .play()
          .catch((err) =>
            console.error("Error playing video after quality change:", err)
          );
      }
    }
  };

  const changePlaybackSpeed = (speed) => {
    const video = videoRef.current;
    if (!video) return;

    video.playbackRate = speed;
    setPlaybackSpeed(speed);
  };

  const toggleSubtitles = () => {
    setShowSubtitles(!showSubtitles);

    if (!showSubtitles && subtitles.length > 0 && !selectedSubtitle) {
      setSelectedSubtitle(subtitles[0].language);
    }
  };

  const selectSubtitle = (language) => {
    setSelectedSubtitle(language);

    const video = videoRef.current;
    if (!video) return;

    // Remove all existing text tracks
    while (video.textTracks.length > 0) {
      video.removeChild(video.textTracks[0]);
    }

    if (language) {
      const subtitle = subtitles.find((s) => s.language === language);
      if (subtitle) {
        const track = document.createElement("track");
        track.kind = "subtitles";
        track.label = subtitle.label;
        track.srclang = subtitle.language;
        track.src = subtitle.src;
        track.default = true;

        video.appendChild(track);

        // Enable the text track
        setTimeout(() => {
          for (let i = 0; i < video.textTracks.length; i++) {
            video.textTracks[i].mode = "showing";
          }
        }, 100);
      }
    }
  };

  const togglePictureInPicture = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPipActive(false);
      } else {
        await video.requestPictureInPicture();
        setIsPipActive(true);
      }
    } catch (error) {
      console.error("PiP error:", error);
    }
  };

  const toggleMiniplayer = () => {
    setIsMiniplayer(!isMiniplayer);
  };

  const addBookmark = () => {
    if (!bookmarks.includes(currentTime)) {
      setBookmarks([...bookmarks, currentTime]);
    }
  };

  const removeBookmark = (time) => {
    setBookmarks(bookmarks.filter((bookmark) => bookmark !== time));
  };

  const jumpToBookmark = (time) => {
    seek(time);
  };

  const jumpToChapter = (chapter) => {
    seek(chapter.startTime);
  };

  const handleProgressBarHover = (e) => {
    const progressBar = progressBarRef.current;
    if (!progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const previewTime = position * duration;

    setShowThumbnail(true);
    setThumbnailPosition(e.clientX - rect.left);

    // In a real implementation, you would generate or fetch the thumbnail for the specific time
    // For this example, we're just using the poster image
    if (thumbnailRef.current && poster) {
      thumbnailRef.current.src = poster;
    }
  };

  const handleProgressBarLeave = () => {
    setShowThumbnail(false);
  };

  // Get current source
  const currentSource =
    sources.find((s) => s.quality === selectedQuality) || sources[0];

  // Calculate progress percentage
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <ConfigProvider
    // theme={{
    //   token: {
    //     colorPrimary: "#6366f1",
    //     borderRadius: 6,
    //   },
    //   algorithm: isDarkMode ? "dark" : "light",
    // }}
    >
      <div
        ref={videoContainerRef}
        className={cn(
          "relative overflow-hidden rounded-lg bg-black",
          isMiniplayer
            ? "fixed bottom-4 right-4 z-50 w-80 shadow-lg"
            : "w-full",
          isDarkMode ? "dark" : ""
        )}
        style={{ aspectRatio: "16/9" }}
      >
        <video
          ref={videoRef}
          className="h-full w-full"
          poster={poster}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline
          onClick={togglePlay}
          onDoubleClick={toggleFullscreen}
        >
          <source src={currentSource.src} type={currentSource.type} />
          {subtitles.map((subtitle) => (
            <track
              key={subtitle.language}
              kind="subtitles"
              label={subtitle.label}
              srcLang={subtitle.language}
              src={subtitle.src}
            />
          ))}
          Your browser does not support the video tag.
        </video>

        {/* Video overlay for title, chapters, etc. */}
        <div
          className={cn(
            "absolute inset-0 flex flex-col justify-between transition-opacity duration-300",
            showControls ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        >
          {/* Top bar */}
          <div className="bg-gradient-to-b from-black/70 to-transparent p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium truncate">{title}</h3>
              {currentChapter && (
                <Badge color="#6366f1" className="text-xs">
                  {currentChapter.title}
                </Badge>
              )}
            </div>
          </div>

          {/* Center play/pause button */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {!isPlaying && (
              <Button
                size="icon"
                variant="secondary"
                className="size-16 rounded-full bg-black/30 backdrop-blur-sm pointer-events-auto"
                onClick={togglePlay}
              >
                <Play className="size-8" />
              </Button>
            )}
          </div>

          {/* Bottom controls */}
          <div className="bg-gradient-to-t from-black/70 to-transparent p-4">
            {/* Progress bar */}
            <div
              ref={progressBarRef}
              className="relative h-1.5 w-full bg-white/30 rounded-full mb-4 cursor-pointer group"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const position = (e.clientX - rect.left) / rect.width;
                seek(position * duration);
              }}
              onMouseMove={handleProgressBarHover}
              onMouseLeave={handleProgressBarLeave}
            >
              <div
                className="absolute top-0 left-0 h-full bg-primary rounded-full"
                style={{ width: `${progressPercentage}%` }}
              />

              {/* Bookmarks */}
              {bookmarks.map((time, index) => (
                <div
                  key={index}
                  className="absolute top-0 h-3 w-1 bg-yellow-400 rounded-full -translate-y-1/4 cursor-pointer"
                  style={{ left: `${(time / duration) * 100}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    jumpToBookmark(time);
                  }}
                />
              ))}

              {/* Chapter markers */}
              {chapters.map((chapter, index) => (
                <div
                  key={index}
                  className="absolute top-0 h-3 w-1 bg-white rounded-full -translate-y-1/4 cursor-pointer"
                  style={{ left: `${(chapter.startTime / duration) * 100}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    jumpToChapter(chapter);
                  }}
                />
              ))}

              {/* Hover thumbnail */}
              {showThumbnail && (
                <div
                  className="absolute bottom-6 bg-black rounded-md overflow-hidden shadow-lg pointer-events-none"
                  style={{
                    left: `${thumbnailPosition}px`,
                    transform: "translateX(-50%)",
                  }}
                >
                  <img
                    ref={thumbnailRef}
                    className="w-32 aspect-video object-cover"
                    alt="Preview"
                  />
                  <div className="bg-black/80 text-white text-xs p-1 text-center">
                    {formatTime(
                      duration *
                        (thumbnailPosition /
                          (progressBarRef.current?.clientWidth || 1))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-white hover:bg-white/10"
                        onClick={togglePlay}
                      >
                        {isPlaying ? (
                          <Pause className="size-5" />
                        ) : (
                          <Play className="size-5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isPlaying ? "Pause (k)" : "Play (k)"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-white hover:bg-white/10"
                        onClick={toggleMute}
                      >
                        {isMuted ? (
                          <VolumeX className="size-5" />
                        ) : (
                          <Volume2 className="size-5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isMuted ? "Unmute (m)" : "Mute (m)"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <div className="w-24 hidden sm:block">
                  <Slider
                    value={[isMuted ? 0 : volume * 100]}
                    min={0}
                    max={100}
                    step={1}
                    onValueChange={(value) => changeVolume(value[0] / 100)}
                    className="cursor-pointer"
                  />
                </div>

                <div className="text-white text-sm ml-2">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-white hover:bg-white/10"
                        onClick={toggleSubtitles}
                      >
                        <Subtitles
                          className={cn(
                            "size-5",
                            showSubtitles ? "text-primary" : "text-white"
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {showSubtitles ? "Disable Subtitles" : "Enable Subtitles"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-white hover:bg-white/10"
                        onClick={togglePictureInPicture}
                      >
                        <PictureInPicture
                          className={cn(
                            "size-5",
                            isPipActive ? "text-primary" : "text-white"
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Picture-in-Picture (i)</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-white hover:bg-white/10"
                        onClick={addBookmark}
                      >
                        <Bookmark className="size-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Add Bookmark (b)</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <DropdownMenu>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-white hover:bg-white/10"
                          >
                            <Settings className="size-5" />
                          </Button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent>Settings</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => setShowSettings(true)}>
                      Advanced Settings
                    </DropdownMenuItem>

                    <DropdownMenuItem className="flex items-center justify-between">
                      <span>Dark Mode</span>
                      <Switch
                        checked={isDarkMode}
                        onCheckedChange={setIsDarkMode}
                      />
                    </DropdownMenuItem>

                    <DropdownMenuItem>
                      <div className="flex flex-col w-full gap-1">
                        <span className="text-sm font-medium">Quality</span>
                        <div className="flex flex-col gap-1">
                          {sources.map((source) => (
                            <Button
                              key={source.quality}
                              variant={
                                selectedQuality === source.quality
                                  ? "default"
                                  : "ghost"
                              }
                              size="sm"
                              className="justify-start"
                              onClick={() => changeQuality(source.quality)}
                            >
                              {source.quality}
                              {selectedQuality === source.quality && (
                                <Check className="ml-auto size-4" />
                              )}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </DropdownMenuItem>

                    <DropdownMenuItem>
                      <div className="flex flex-col w-full gap-1">
                        <span className="text-sm font-medium">
                          Playback Speed
                        </span>
                        <div className="flex flex-col gap-1">
                          {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                            <Button
                              key={speed}
                              variant={
                                playbackSpeed === speed ? "default" : "ghost"
                              }
                              size="sm"
                              className="justify-start"
                              onClick={() => changePlaybackSpeed(speed)}
                            >
                              {speed}x
                              {playbackSpeed === speed && (
                                <Check className="ml-auto size-4" />
                              )}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-white hover:bg-white/10"
                        onClick={toggleMiniplayer}
                      >
                        <ExternalLink
                          className={cn(
                            "size-5",
                            isMiniplayer ? "text-primary" : "text-white"
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isMiniplayer ? "Exit Mini Player" : "Mini Player"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-white hover:bg-white/10"
                        onClick={toggleFullscreen}
                      >
                        {isFullscreen ? (
                          <Minimize className="size-5" />
                        ) : (
                          <Maximize className="size-5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isFullscreen ? "Exit Fullscreen (f)" : "Fullscreen (f)"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </div>

        {/* Settings drawer */}
        <Drawer
          title="Video Settings"
          placement="right"
          open={showSettings}
          onClose={() => setShowSettings(false)}
          width={320}
        >
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="Quality" key="1">
              <div className="flex flex-col gap-2">
                {sources.map((source) => (
                  <Button
                    key={source.quality}
                    variant={
                      selectedQuality === source.quality ? "default" : "outline"
                    }
                    className="w-full justify-start"
                    onClick={() => {
                      changeQuality(source.quality);
                      setShowSettings(false);
                    }}
                  >
                    {source.quality}
                    {selectedQuality === source.quality && (
                      <Check className="ml-auto size-4" />
                    )}
                  </Button>
                ))}
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Playback" key="2">
              <div className="flex flex-col gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Playback Speed</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                      <Button
                        key={speed}
                        variant={
                          playbackSpeed === speed ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          changePlaybackSpeed(speed);
                          setShowSettings(false);
                        }}
                      >
                        {speed}x
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-2">Loop</h4>
                  <div className="flex items-center">
                    <Switch
                      checked={loop}
                      onCheckedChange={(checked) => {
                        const video = videoRef.current;
                        if (video) {
                          video.loop = checked;
                        }
                      }}
                    />
                    <span className="ml-2">Repeat video</span>
                  </div>
                </div>
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Subtitles" key="3">
              <div className="flex flex-col gap-2">
                <Button
                  variant={!selectedSubtitle ? "default" : "outline"}
                  className="w-full justify-start"
                  onClick={() => {
                    selectSubtitle(null);
                    setShowSettings(false);
                  }}
                >
                  Off
                  {!selectedSubtitle && <Check className="ml-auto size-4" />}
                </Button>

                {subtitles.map((subtitle) => (
                  <Button
                    key={subtitle.language}
                    variant={
                      selectedSubtitle === subtitle.language
                        ? "default"
                        : "outline"
                    }
                    className="w-full justify-start"
                    onClick={() => {
                      selectSubtitle(subtitle.language);
                      setShowSettings(false);
                    }}
                  >
                    {subtitle.label}
                    {selectedSubtitle === subtitle.language && (
                      <Check className="ml-auto size-4" />
                    )}
                  </Button>
                ))}
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Bookmarks" key="4">
              {bookmarks.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {bookmarks.map((time, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <Button
                        variant="ghost"
                        onClick={() => {
                          jumpToBookmark(time);
                          setShowSettings(false);
                        }}
                      >
                        {formatTime(time)}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => removeBookmark(time)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="size-4"
                        >
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  No bookmarks added yet. Press 'b' or click the bookmark button
                  to add one.
                </p>
              )}
            </Tabs.TabPane>

            <Tabs.TabPane tab="Chapters" key="5">
              {chapters.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {chapters.map((chapter, index) => (
                    <Button
                      key={chapter.id}
                      variant={
                        currentChapter?.id === chapter.id
                          ? "default"
                          : "outline"
                      }
                      className="w-full justify-start"
                      onClick={() => {
                        jumpToChapter(chapter);
                        setShowSettings(false);
                      }}
                    >
                      <div className="flex flex-col items-start">
                        <span>{chapter.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatTime(chapter.startTime)}
                        </span>
                      </div>
                      {currentChapter?.id === chapter.id && (
                        <ChevronRight className="ml-auto size-4" />
                      )}
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  No chapters available for this video.
                </p>
              )}
            </Tabs.TabPane>
          </Tabs>
        </Drawer>
      </div>
    </ConfigProvider>
  );
};

export default CustomPlayer;
